import { useEffect, useRef } from 'react'
import {
  loadPredictions,
  loadBankerGameId,
  loadCurrentGameweek,
  loadGameweekPhases,
  loadSeasonHistory,
  loadSeasonDetails,
  type GameweekPhase,
} from '../storage'
import { loadLeagues } from '../api/leagues'
import { refreshResults } from '../seasonData'
import {
  createRealtimeConnection,
  type RealtimeStatus,
  type WebSocketLike,
} from './realtimeSocket'
import type { RealtimePhasePayload } from '../realtime/protocol'
import type { GameweekStateController } from './useGameweekState'
import type { PredictionsController } from './usePredictions'
import type { LeaguesController } from './useLeagues'
import type { SeasonArchiveController } from './useSeasonArchive'

/**
 * The **fallback** cadence. Phase changes are pushed over `/api/realtime` now; this
 * interval only runs when the socket could not be opened twice — a proxy missing its Upgrade
 * headers, most likely, which fails as a clean degrade rather than as anything visibly broken.
 *
 * It is still the number it was, and the path is still fully tested, because
 * "degraded and honest" is only true while the degraded path actually works.
 */
export const GAMEWEEK_PHASE_POLL_MS = 4000

/**
 * Stable fingerprint of the shared phase for a client on `gameweek` — the deadline-passed set, the
 * settled set, the results-revealed flag, and **any gameweek's scored digest**.
 *
 * The digests are what make live scoring visible without a reload. Every gameweek counts, not just
 * the current one: a correction to last week moves the season total too.
 */
export function phaseSignature(gameweek: number, phases: GameweekPhase[]): string {
  const flags = [...phases]
    .sort((a, b) => a.gameweek - b.gameweek)
    .map((p) => `${p.gameweek}:${p.deadlinePassed ? 'd' : ''}${p.resultsRevealed ? 'r' : ''}:${p.scoredDigest ?? ''}`)
  return `${gameweek}|${flags.join(',')}`
}

export interface AppliedPhaseTracker {
  /** Records a phase as already reflected on screen, without treating it as a change. */
  record: (gameweek: number, phases: GameweekPhase[]) => void
  /** True — and records it — when this phase differs from the last one applied. */
  takeIfChanged: (gameweek: number, phases: GameweekPhase[]) => boolean
}

/**
 * The last shared phase this client has applied. Shared by the initial load (which seeds it, so a
 * fresh page does not immediately re-sync what it just fetched) and the poll (which compares
 * against it), which is why it is its own hook rather than a ref inside either.
 */
export function useAppliedPhase(): AppliedPhaseTracker {
  const lastSignatureRef = useRef<string | null>(null)
  return {
    record: (gameweek, phases) => {
      lastSignatureRef.current = phaseSignature(gameweek, phases)
    },
    takeIfChanged: (gameweek, phases) => {
      const signature = phaseSignature(gameweek, phases)
      if (signature === lastSignatureRef.current) return false
      lastSignatureRef.current = signature
      return true
    },
  }
}

export interface UseGameweekPhasePollOptions {
  /**
   * The admin's own "play Live" animation is running. Frames that land now are held, not dropped
   * — see the hook's docblock; this is the one behaviour the socket did not inherit for free.
   */
  isSimulating: boolean
  /** Only connect once the app is actually up and signed in. A signed-out client gets no socket. */
  ready: boolean
  appliedPhase: AppliedPhaseTracker
  gameweek: GameweekStateController
  predictions: PredictionsController
  season: SeasonArchiveController
  leagues: LeaguesController
  /**
   * Announces that the fetched season store changed, exactly as the live poll's callback does —
   * the store lives outside React, so a refresh has to be signalled for anything to re-render.
   */
  onResultsRefreshed: () => void
  /** Injected by the tests so frames, drops and reconnects can be driven without a network. */
  socketFactory?: (url: string) => WebSocketLike
  /** Observability seam: 'live' | 'polling' | 'refused' | …. Nothing renders it today. */
  onRealtimeStatus?: (status: RealtimeStatus) => void
  /** Injected by the tests so the reconnect ladder can be walked without waiting out its backoff. */
  realtimeSchedule?: (run: () => void, ms: number) => () => void
}

/**
 * Applies a phase table to the stores, when it differs from the last one applied.
 *
 * Shared by both delivery paths: a pushed frame and a fallback poll tick hand the
 * same two arguments in, so the socket changed only *how the client learns something changed*, not
 * what it does about it. `gameweek` is passed rather than fetched because the poll already has it
 * from its own probe and re-reading it would be a second request per tick.
 */
async function applyPhaseChange(
  o: UseGameweekPhasePollOptions,
  phases: GameweekPhase[],
  gameweek: number
): Promise<void> {
  const current = o.gameweek.currentGameweek
  if (!o.appliedPhase.takeIfChanged(gameweek, phases)) return

  const [predictions, banker] = await Promise.all([
    loadPredictions(gameweek),
    loadBankerGameId(gameweek),
  ])
  o.gameweek.setDeadlinePassedGameweeks(
    phases.filter((p) => p.deadlinePassed).map((p) => p.gameweek)
  )
  o.gameweek.setSettledGameweeks(phases.filter((p) => p.resultsRevealed).map((p) => p.gameweek))
  o.gameweek.setResultsRevealed(
    phases.find((p) => p.gameweek === gameweek)?.resultsRevealed ?? false
  )
  o.predictions.setPredictions(predictions)
  o.predictions.setBankerGameId(banker)
  if (gameweek !== current) o.gameweek.setCurrentGameweek(gameweek)

  // Re-read on **any** phase change, not only when the gameweek advanced. The season archive and
  // the league tables derive from `season_history`, which the server rewrites every time a fixture
  // reaches full time, so a signature that moved on a digest is exactly the missed case.
  //
  // The results store comes with it. The live poll also refreshes it, but only inside its own
  // plausible live window, so the grid's scores cannot depend on it. The server having rescored is
  // the authoritative statement that a result landed.
  const [history, details, leagues] = await Promise.all([
    loadSeasonHistory(),
    loadSeasonDetails(),
    loadLeagues(),
    refreshResults(),
  ])
  o.season.setSeasonHistory(history)
  o.season.setSeasonDetails(details)
  o.leagues.setLeagues(leagues)
  o.onResultsRefreshed()
}

/** The pushed path: the frame carries the phases, so only the per-user gameweek is fetched. */
async function applyPushedPhases(
  o: UseGameweekPhasePollOptions,
  phases: GameweekPhase[]
): Promise<void> {
  try {
    const state = await loadCurrentGameweek(o.gameweek.currentGameweek)
    await applyPhaseChange(o, phases, state.gameweek)
  } catch {
    // A frame we could not act on. The next one carries full state again, and a reconnect
    // re-subscribes — there is nothing to retry here that would not be retried anyway.
  }
}

/**
 * Keeps the shared gameweek phase in sync so an admin dev action and a full-time rescore surface
 * for EVERY signed-in user. On a real change it re-syncs the affected view.
 *
 * **Push, with the poll as fallback.** The client subscribes to the `phase` topic instead of asking
 * 900 times an hour; the server answers the subscribe with current state, so a reconnect self-heals
 * in one round trip. Two failed socket opens fall back to `GAMEWEEK_PHASE_POLL_MS`.
 *
 * **`isSimulating`.** A pushed frame arrives once and is gone, so one landing mid-"play Live"
 * animation is held and applied when the animation ends rather than dropped.
 */
export function useGameweekPhasePoll(options: UseGameweekPhasePollOptions): void {
  const latest = useRef(options)
  useEffect(() => {
    latest.current = options
  })

  // The frame that arrived while the "play Live" animation was running. One slot, not a queue:
  // every phase frame carries the whole phase table, so the newest one supersedes the rest.
  const held = useRef<GameweekPhase[] | null>(null)

  useEffect(() => {
    if (!options.ready) return

    let pollTimer: ReturnType<typeof setInterval> | null = null
    /** Set once the ladder hands over. Sticky, so a hidden→visible cycle resumes the poll and does
     *  not quietly try the socket again on a proxy that has already refused it twice. */
    let fellBack = false

    const pollOnce = () => {
      const o = latest.current
      if (!o.ready || o.isSimulating) return
      void (async () => {
        try {
          const [gameweekState, phases] = await Promise.all([
            loadCurrentGameweek(o.gameweek.currentGameweek),
            loadGameweekPhases(),
          ])
          await applyPhaseChange(o, phases, gameweekState.gameweek)
        } catch {
          // Transient poll failure — try again on the next tick.
        }
      })()
    }

    const startPolling = () => {
      fellBack = true
      if (pollTimer) return
      pollTimer = setInterval(pollOnce, GAMEWEEK_PHASE_POLL_MS)
    }

    const connection = createRealtimeConnection<RealtimePhasePayload>({
      topic: 'phase',
      socketFactory: options.socketFactory,
      schedule: options.realtimeSchedule,
      onStatus: options.onRealtimeStatus,
      onFallback: startPolling,
      onFrame: (phases) => {
        const o = latest.current
        if (!o.ready) return
        if (o.isSimulating) {
          held.current = phases
          return
        }
        void applyPushedPhases(o, phases)
      },
    })

    connection.open()

    // Most of the battery win, and free once the server answers a subscribe with current state:
    // a hidden tab holds no socket at all, and the round trip on the way back replaces every
    // frame it missed. A tab that already fell back to polling pauses that instead.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        connection.suspend()
        if (pollTimer) {
          clearInterval(pollTimer)
          pollTimer = null
        }
        return
      }
      if (fellBack) {
        // Read once on the way back rather than waiting out an interval — the tab may have been
        // hidden across a full-time whistle, and this is the polling path's version of the round
        // trip a re-subscribe gets for free.
        pollOnce()
        startPolling()
        return
      }
      connection.open()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      connection.stop()
      if (pollTimer) clearInterval(pollTimer)
    }
  }, [options.ready, options.socketFactory, options.onRealtimeStatus, options.realtimeSchedule])

  // The held frame, released the moment the animation stops. Runs on the flip to false only —
  // `held.current` is cleared as it is taken, so a re-render cannot apply it twice.
  useEffect(() => {
    if (options.isSimulating) return
    const pending = held.current
    if (!pending) return
    held.current = null
    void applyPushedPhases(latest.current, pending)
  }, [options.isSimulating])
}
